import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { WhatsappConversation, WhatsappMessage } from '@marnic/shared';
import { useWhatsapp } from '../store/whatsapp';

export interface ConversationsResponse {
  total: number;
  items: WhatsappConversation[];
}

export interface MessagesResponse {
  items: WhatsappMessage[];
}

export function useConversations(filters: { status?: string; leadId?: string } = {}) {
  const setConversations = useWhatsapp((s) => s.setConversations);
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.leadId) params.set('leadId', filters.leadId);

  const query = useQuery({
    queryKey: ['whatsapp', 'conversations', filters.status ?? '', filters.leadId ?? ''],
    queryFn: () => api.get<ConversationsResponse>(`/whatsapp/conversations?${params.toString()}`),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (query.data) setConversations(query.data.items);
  }, [query.data, setConversations]);

  return query;
}

export function useConversationMessages(conversationId: string | null) {
  const setMessages = useWhatsapp((s) => s.setMessages);

  const query = useQuery({
    queryKey: ['whatsapp', 'messages', conversationId ?? ''],
    queryFn: () =>
      api.get<MessagesResponse>(`/whatsapp/conversations/${conversationId}/messages?limit=100`),
    enabled: !!conversationId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data && conversationId) setMessages(conversationId, query.data.items);
  }, [query.data, conversationId, setMessages]);

  return query;
}

export function usePatchConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { status?: 'open' | 'closed' | 'snoozed'; agentId?: string | null }) =>
      api.patch<{ ok: boolean }>(`/whatsapp/conversations/${conversationId}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp', 'conversations'] });
    },
  });
}
